export const normalizeTownFromLookup = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return trimmed.toLowerCase() === "na" ? "na" : trimmed;
};

export const hasInvalidPartyLocationValue = (value: unknown): boolean => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed === "") {
    return value.length > 0; // Return true only if string was whitespace-only
  }
  const normalized = trimmed.toLowerCase();
  if (normalized === "na") return false;
  return ["n/a", "not available", "null"].includes(normalized);
};

export const getCountryCodeFromState = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const normalized = value.trim().toUpperCase();
  const match = normalized.match(/^([A-Z]{2})(?:-[A-Z0-9]{1,3})?$/);
  return match?.[1] || "";
};

export const filterCountryOptionsByState = <T extends { value?: unknown }>(
  options: T[],
  stateCountryCode: unknown
): T[] => {
  const countryCode = getCountryCodeFromState(stateCountryCode);
  if (!countryCode) return options;
  return options.filter(
    (option) =>
      typeof option?.value === "string" &&
      option.value.trim().toUpperCase() === countryCode
  );
};

export const unwrapScSearchItems = (payload: any): any[] => {
  let value = payload?.response ?? payload;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (value?.response !== undefined) return unwrapScSearchItems(value.response);
  return Array.isArray(value) ? value : [];
};

type DateOnly = {
  iso: string;
  time: number;
};

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

export const parseDateOnly = (value: unknown): DateOnly | null => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = value.getDate();
    return {
      iso: `${year.toString().padStart(4, "0")}-${month
        .toString()
        .padStart(2, "0")}-${day.toString().padStart(2, "0")}`,
      time: Date.UTC(year, month - 1, day),
    };
  }

  const strValue = String(value).trim();
  const match = strValue.match(DATE_ONLY_PATTERN);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const time = Date.UTC(year, month - 1, day);
  const parsed = new Date(time);

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return { iso: `${match[1]}-${match[2]}-${match[3]}`, time };
};

export const validateTcIssueDateAgainstSc = (
  tcIssueDate: unknown,
  sc: Record<string, any> | null | undefined
): string | null => {
  if (!tcIssueDate || !sc) return null;

  const issueDateObj = parseDateOnly(tcIssueDate);
  if (!issueDateObj) return null;

  // Determine version: SC v3 vs SC v4
  const rawVersion = String(
    sc.scVersion || sc.version || sc.scVersionNo || sc.scSchemaVersion || ""
  )
    .trim()
    .toUpperCase();
  const isV3 = rawVersion.includes("3");

  // Parse candidate start dates:
  // For SC v3: scIssueDate <= tcIssueDate
  // For SC v4: scValidFromDate <= tcIssueDate
  const scIssueDateObj = parseDateOnly(
    sc.scIssueDate || sc.scIssue || sc.issueDate || sc.scCreatedDate || sc.createdDate
  );
  const scValidFromDateObj = parseDateOnly(
    sc.scValidFrom || sc.scValidFromDate || sc.validFrom || sc.validFromDate
  );

  let startDateObj = isV3 ? scIssueDateObj : scValidFromDateObj;
  let startLabel = isV3 ? "Seller SC Issue Date" : "Seller SC Valid From Date";

  // Fallbacks if version-specific start date is missing
  if (!startDateObj) {
    if (isV3 && scValidFromDateObj) {
      startDateObj = scValidFromDateObj;
      startLabel = "Seller SC Valid From Date";
    } else if (!isV3 && scIssueDateObj) {
      startDateObj = scIssueDateObj;
      startLabel = "Seller SC Issue Date";
    }
  }

  // Parse candidate end dates:
  // If scExtendedUntilDate is provided, tcIssueDate <= scExtendedUntilDate
  // Otherwise, tcIssueDate <= scValidUntilDate
  const scExtendedUntilObj = parseDateOnly(
    sc.scValidExtendedUntil ||
      sc.scExtendedUntilDate ||
      sc.scValidExtendedUntilDate ||
      sc.extendedUntil ||
      sc.extendedUntilDate
  );
  const scValidUntilObj = parseDateOnly(
    sc.scValidUntil || sc.scValidUntilDate || sc.validUntil || sc.validUntilDate
  );

  let endDateObj: DateOnly | null = null;
  let endLabel = "";

  if (scExtendedUntilObj) {
    endDateObj = scExtendedUntilObj;
    endLabel = "Seller SC Extended Until Date";
  } else if (scValidUntilObj) {
    endDateObj = scValidUntilObj;
    endLabel = "Seller SC Valid Until Date";
  }

  // Validation checks:
  if (startDateObj && issueDateObj.time < startDateObj.time) {
    return `TC Issue Date cannot be before the ${startLabel} (${startDateObj.iso})`;
  }

  if (endDateObj && issueDateObj.time > endDateObj.time) {
    return `TC Issue Date must be on or before the ${endLabel} (${endDateObj.iso}). This Seller SC cannot be selected.`;
  }

  return null;
};

